import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import { PhArrowLeft } from 'ember-phosphor-icons';
import { VStack } from 'tio-ui/components/layout';
import ScheduledExportsList from './scheduled-exports-list';

export interface ScheduledExportsPageSignature {
  Args: {
    /** Company whose scheduled-export runs to list. */
    companyId: string;
    /**
     * Optional route to link back to (the reports page the schedule was
     * created from). Omit for a standalone entry with no parent.
     */
    backRoute?: string;
    /** Dynamic-segment model for `backRoute`, when that route needs one. */
    backModel?: string;
    /** Label for the back link (defaults to "Reports"). */
    backLabel?: string;
    /** Group runs under their schedule instead of one flat list — see the list component. */
    groupBySchedule?: boolean;
  };
}

/**
 * Full-page host for {@link ScheduledExportsList}. The single page-level
 * `<PageHeader>` / `<h1>` is owned by the route shell (in tio-admin, the
 * companies/show route renders the company header + tabs and yields an outlet),
 * so this sub-page renders its identity as an `<h2>` — matching the sibling
 * reports page's section headings — plus a back link, rather than adding a
 * second `<h1>`. Bare copy lives here (tio-reporting isn't bare-string linted)
 * for the eventual i18n pass.
 *
 * This is a *destination*, not the aggregate Stats popup: a listing with
 * per-run download links that will grow (paging, filters, sent-status), so it
 * lives at its own URL rather than in a modal.
 */
const ScheduledExportsPage: TOC<ScheduledExportsPageSignature> = <template>
  <VStack>
    {{#if @backRoute}}
      {{#if @backModel}}
        <LinkTo
          @route={{@backRoute}}
          @model={{@backModel}}
          class="tio-inline-row-extra-tight w-fit tio-text-sm-medium tio-fg-info-h hover:tio-fg-info"
          data-test-scheduled-exports-back
        >
          <PhArrowLeft class="h-4 w-4" />
          {{if @backLabel @backLabel "Reports"}}
        </LinkTo>
      {{else}}
        <LinkTo
          @route={{@backRoute}}
          class="tio-inline-row-extra-tight w-fit tio-text-sm-medium tio-fg-info-h hover:tio-fg-info"
          data-test-scheduled-exports-back
        >
          <PhArrowLeft class="h-4 w-4" />
          {{if @backLabel @backLabel "Reports"}}
        </LinkTo>
      {{/if}}
    {{/if}}

    <h2 class="tio-text-lg-semibold tio-fg-primary" data-test-scheduled-exports-page-title>
      Scheduled exports
    </h2>

    <ScheduledExportsList @companyId={{@companyId}} @groupBySchedule={{@groupBySchedule}} />
  </VStack>
</template>;

export default ScheduledExportsPage;
